import {
  AUTO_REVIEW_RULE_BEHAVIORS,
  MAX_AUTO_REVIEW_RULE_CHARS,
  MAX_AUTO_REVIEW_RULES,
  type AutoReviewRule,
  type AutoReviewRuleBehavior,
} from '@littlebird/sdk';
import { observer } from 'mobx-react-lite';
import { useState } from 'react';

import { Icons } from '@/assets/icons/icons';
import { Button } from '@/components/ui/Button';
import { DropdownMenu } from '@/components/ui/dropdown-menu';
import { Input } from '@/components/ui/Input';
import { XStack, YStack } from '@/components/ui/layout/layout';
import { FEATURES, hasFeature } from '@/features/feature-flags';
import { cn } from '@/lib/utils';

import { SettingsSection } from '../settings-section';

import { useAutoReviewRules } from './settings-auto-review.hooks';

const BEHAVIOR_LABELS: Record<AutoReviewRuleBehavior, string> = {
  auto_run: 'Auto run',
  ask_first: 'Ask first',
};

export const AutoReviewSettings = observer(function AutoReviewSettings() {
  const { rules, canAddRule, addRule, updateRule, removeRule } =
    useAutoReviewRules();
  const [editingRuleId, setEditingRuleId] = useState<string | null>(null);

  if (!hasFeature(FEATURES.AUTO_REVIEW)) return null;

  return (
    <SettingsSection.Root>
      <SettingsSection.Header>
        <SettingsSection.Title>Auto-review</SettingsSection.Title>
        <SettingsSection.Description>
          Write one short rule for each action. When an &ldquo;Ask first&rdquo;
          rule and an &ldquo;Auto run&rdquo; rule both cover an action,
          Littlebird asks first.
        </SettingsSection.Description>
      </SettingsSection.Header>
      <SettingsSection.Group>
        <SettingsSection.Item direction="vertical" searchId="auto-review.rules">
          <RuleForm
            submitLabel="Add rule"
            disabled={!canAddRule}
            onSubmit={addRule}
          />
          {!canAddRule && (
            <SettingsSection.ItemDescription>
              You can have up to {MAX_AUTO_REVIEW_RULES} rules. Delete one to
              add another.
            </SettingsSection.ItemDescription>
          )}
        </SettingsSection.Item>
      </SettingsSection.Group>
      {rules.length > 0 && (
        <SettingsSection.Group title="Rules">
          {rules.map((rule, index) => (
            <YStack key={rule.id} className="gap-5">
              {index > 0 && <SettingsSection.Seperator />}
              {rule.id === editingRuleId ? (
                <RuleForm
                  initialRule={rule}
                  submitLabel="Save"
                  onSubmit={(text, behavior) => {
                    updateRule(rule.id, text, behavior);
                    setEditingRuleId(null);
                  }}
                  onCancel={() => setEditingRuleId(null)}
                />
              ) : (
                <RuleRow
                  rule={rule}
                  onEdit={() => setEditingRuleId(rule.id)}
                  onRemove={() => removeRule(rule.id)}
                />
              )}
            </YStack>
          ))}
        </SettingsSection.Group>
      )}
    </SettingsSection.Root>
  );
});

const RuleForm = ({
  initialRule,
  submitLabel,
  disabled = false,
  onSubmit,
  onCancel,
}: {
  initialRule?: AutoReviewRule;
  submitLabel: string;
  disabled?: boolean;
  onSubmit: (text: string, behavior: AutoReviewRuleBehavior) => void;
  onCancel?: () => void;
}) => {
  const [text, setText] = useState(initialRule?.text ?? '');
  const [behavior, setBehavior] = useState<AutoReviewRuleBehavior>(
    initialRule?.behavior ?? 'auto_run'
  );
  const canSubmit = !disabled && text.trim().length > 0;

  const submit = () => {
    if (!canSubmit) return;
    onSubmit(text, behavior);
    setText('');
  };

  return (
    <YStack className="w-full gap-3">
      <Input
        label="When Littlebird wants to:"
        placeholder="e.g. reply to emails for me"
        value={text}
        maxLength={MAX_AUTO_REVIEW_RULE_CHARS}
        disabled={disabled}
        onChange={(event) => setText(event.target.value)}
        onKeyDown={(event) => {
          // Enter or Escape while composing (IME) edits the composed text
          if (event.nativeEvent.isComposing) return;
          if (event.key === 'Enter') submit();
          if (event.key === 'Escape') onCancel?.();
        }}
      />
      <XStack className="w-full items-end justify-between gap-3">
        <YStack className="gap-2">
          <span className="text-sm text-foreground">It should:</span>
          <BehaviorMenu
            behavior={behavior}
            disabled={disabled}
            onSelect={setBehavior}
          />
        </YStack>
        <XStack className="gap-2">
          {onCancel && (
            <Button variant="ghost" onClick={onCancel}>
              Cancel
            </Button>
          )}
          <Button variant="secondary" disabled={!canSubmit} onClick={submit}>
            {submitLabel}
          </Button>
        </XStack>
      </XStack>
    </YStack>
  );
};

const BehaviorMenu = ({
  behavior,
  disabled,
  onSelect,
}: {
  behavior: AutoReviewRuleBehavior;
  disabled: boolean;
  onSelect: (behavior: AutoReviewRuleBehavior) => void;
}) => (
  <DropdownMenu.Root>
    <DropdownMenu.Trigger asChild>
      <Button variant="outline" disabled={disabled} className="gap-1.5">
        {BEHAVIOR_LABELS[behavior]}
        <Icons.chevronDown className="size-3.5 text-foreground-muted" />
      </Button>
    </DropdownMenu.Trigger>
    <DropdownMenu.Content align="start" fit>
      {AUTO_REVIEW_RULE_BEHAVIORS.map((option) => (
        <DropdownMenu.Item
          key={option}
          onSelect={() => onSelect(option)}
          className="min-w-32"
        >
          <Icons.check
            className={cn('size-3.5', option !== behavior && 'invisible')}
          />
          <span className="flex-1">{BEHAVIOR_LABELS[option]}</span>
        </DropdownMenu.Item>
      ))}
    </DropdownMenu.Content>
  </DropdownMenu.Root>
);

const RuleRow = ({
  rule,
  onEdit,
  onRemove,
}: {
  rule: AutoReviewRule;
  onEdit: () => void;
  onRemove: () => void;
}) => (
  <SettingsSection.Item>
    <span className="min-w-0 flex-1 break-words text-sm text-foreground">
      {rule.text}
    </span>
    <XStack className="shrink-0 items-center gap-2">
      <span className="w-24 text-sm text-foreground-muted">
        {BEHAVIOR_LABELS[rule.behavior]}
      </span>
      <Button
        variant="ghost"
        size="icon"
        iconOnly
        aria-label={`Edit the rule "${rule.text}"`}
        title="Edit"
        onClick={onEdit}
      >
        <Icons.pencil />
      </Button>
      <Button
        variant="destructive-ghost"
        size="icon"
        iconOnly
        aria-label={`Delete the rule "${rule.text}"`}
        title="Delete"
        onClick={onRemove}
      >
        <Icons.trash2 />
      </Button>
    </XStack>
  </SettingsSection.Item>
);
